import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Typography } from '../../ui/Typography/Typography'
import ThreeDots from '../../../assets/icons/ThreeDots'
import { numberOfMembers } from '../../../theme/reuseables'

import {
    IkonAfvissag,
    IkonAktivreminder,
    IkonFjernreminder,
    IkonPassivreminder,
    IkonSammenlaeg,
    Info,
} from '../../../assets/icons'
import { SortCasesProperties } from '../../../utils/sortCasesHelper'
// Import Swiper styles
import { formatDate } from '../../../utils/format'
import {
    getCaseStatusColorCode,
    getCaseStatusLabel,
} from '../../../utils/utils'
import { Badge } from '../../ui/Badge/Badge'
import {
    Card,
    CardCell,
    CardContentMobile,
    CardHeader,
    CardHeaderCell,
    CardList,
    CardMobile,
    Circle,
    CircleText,
} from '../../ui/Card'
import { Case } from '../../../models/Case'
import { SortCasesButton as SortButton } from '../../ui/SortButton'
import { colors } from '../../../theme/palette'
import {
    filteredCasesAtom,
    fetchCasesAtom,
    isFetchingCasesAtom,
    openCasesAtom,
    sortedOpenCasesAtom,
} from '../../../store/filter/filterCasesAtom'
import useSortCasesList from '../../../hooks/useSortCasesList'
import { L, L_HTML } from '../../providers/Lokalise'
import { Button } from '../../ui/Button/Button'
import { Dropdown } from '../../ui/Dropdown'
import { AttachToAnotherCase } from './AttachToAnotherCase'
import { Tooltip } from '../../ui/Tooltip/Tooltip'
import { RejectCase } from './RejectCase'
import RemoveReminder from './Reminders/RemoveReminder'
import { profileAtom } from '../../../api/profile/profile'
import { getInitials } from '../../../utils/initials'
import breakpoints from '../../../theme/breakpoints'
import Switch from '../../ui/Switch'
// Should be 8 columns out of 100%
const gridTemplateColumns = '13% 18% 18% 17% 16% 10% 4% 4%'

const useStyles = createUseStyles({
    numberOfMembers: {
        ...numberOfMembers,
    },
    dropComponent: {
        display: 'block',
        fontSize: '0.875rem',
        padding: '0',
        borderRadius: '0 0 8px 0',
        border: 'none',
        margin: '0',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            padding: '0.5em 1.75em',
        },
    },
    dropComponentIcon: {
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            rotate: '90deg',
        },
    },
})

const DropdownComponent = ({
    currentCase,
}: {
    currentCase: Case
}): JSX.Element => {
    const [attachCaseOpen, setAttachCaseOpen] = useState(false)
    const [rejectCaseOpen, setRejectCaseOpen] = useState(false)
    const [removeReminderOpen, setRemoveReminderOpen] = useState(false)
    const [, fetchCases] = useAtom(fetchCasesAtom)
    const [authProfile] = useAtom(profileAtom)
    const [openCases] = useAtom(openCasesAtom)
    const classes = useStyles()

    return (
        <>
            <Dropdown
                align="right"
                trigger={
                    <span className={classes.dropComponent}>
                        <Button type="ghost">
                            <ThreeDots
                                width="22px"
                                className={classes.dropComponentIcon}
                            />
                        </Button>
                    </span>
                }
                items={[
                    ...(currentCase.reminder?.status
                        ? [
                              {
                                  id: 'RemoveReminder',
                                  label: L('LØN_OVERSIGT_FJERN_REMINDER_LABEL'),
                                  onClick: () => setRemoveReminderOpen(true),
                                  icon: <IkonFjernreminder />,
                                  disabled: authProfile.isImitator,
                              },
                          ]
                        : []),
                    ...(currentCase.allowedUpdateActionList.includes(
                        'SammenfletMedAndenSag'
                    )
                        ? [
                              {
                                  id: 'Attach',
                                  label: L(
                                      'LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_LABEL'
                                  ),
                                  onClick: () =>
                                      setAttachCaseOpen(!attachCaseOpen),
                                  icon: <IkonSammenlaeg />,
                                  disabled: authProfile.isImitator,
                              },
                          ]
                        : []),
                    ...(currentCase.allowedUpdateActionList.includes('Afvis')
                        ? [
                              {
                                  id: 'delete',
                                  label: L('LØN_OVERSIGT_AFVIS_SAG_LABEL'),
                                  color: colors.red600,
                                  icon: <IkonAfvissag fill={colors.red600} />,
                                  onClick: () => setRejectCaseOpen(true),
                                  disabled: authProfile.isImitator,
                              },
                          ]
                        : []),
                ]}
                style={{
                    fontSize: '0.875rem',
                }}
            />
            {attachCaseOpen && (
                <AttachToAnotherCase
                    currentCase={currentCase}
                    onCancel={() => {
                        setAttachCaseOpen(false)
                    }}
                    onSuccess={() => {
                        setAttachCaseOpen(false)
                        fetchCases()
                    }}
                    openCases={openCases}
                />
            )}
            {rejectCaseOpen &&
                currentCase.allowedUpdateActionList.includes('Afvis') && (
                    <RejectCase
                        caseId={currentCase.caseId}
                        caseRegistrationDate={currentCase.caseRegistrationDate}
                        open={!!rejectCaseOpen}
                        handleClose={() => setRejectCaseOpen(false)}
                        onSuccess={() => {
                            setRejectCaseOpen(false)
                            fetchCases()
                        }}
                    />
                )}
            {removeReminderOpen && (
                <RemoveReminder
                    caseRegistrationDate={currentCase.caseRegistrationDate}
                    caseId={currentCase.caseId}
                    reminderId={currentCase.reminder?.id}
                    onCancel={() => setRemoveReminderOpen(false)}
                    onSuccess={() => {
                        setRemoveReminderOpen(false)
                        fetchCases()
                    }}
                />
            )}
        </>
    )
}

// Add keyframes animation
const style = document.createElement('style')
style.textContent = `
    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
`
document.head.appendChild(style)

export const CaseCardSkeleton = (): JSX.Element => {
    return (
        <Card gridTemplateColumns={gridTemplateColumns}>
            <CardCell>
                <div
                    style={{
                        width: '80%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="small">
                <div
                    style={{
                        width: '70%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="small">
                <div
                    style={{
                        width: '80%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="small">
                <div
                    style={{
                        width: '60%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="small">
                <div
                    style={{
                        width: '40%',
                        height: '24px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="small">
                <div
                    style={{
                        width: '70%',
                        height: '16px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="none">
                <div
                    style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardCell paddingSize="none">
                <div
                    style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: colors.gray,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
        </Card>
    )
}

// Helper function to create skeleton placeholders
const createSkeletonPlaceholder = (
    width: string,
    height = '16px'
): JSX.Element => (
    <div
        style={{
            width,
            height,
            backgroundColor: colors.gray,
            borderRadius: '4px',
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        <div
            style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                transform: 'translateX(-100%)',
                backgroundImage: `linear-gradient(
                    90deg,
                    rgba(255, 255, 255, 0) 0,
                    rgba(255, 255, 255, 0.2) 20%,
                    rgba(255, 255, 255, 0.5) 60%,
                    rgba(255, 255, 255, 0)
                )`,
                animation: 'shimmer 1.2s infinite',
            }}
        />
    </div>
)

export const CaseCardSkeletonMobile = (): JSX.Element => {
    return (
        <CardMobile>
            <CardCell>
                <div
                    style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: colors.gray,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            transform: 'translateX(-100%)',
                            backgroundImage: `linear-gradient(
                                90deg,
                                rgba(255, 255, 255, 0) 0,
                                rgba(255, 255, 255, 0.2) 20%,
                                rgba(255, 255, 255, 0.5) 60%,
                                rgba(255, 255, 255, 0)
                            )`,
                            animation: 'shimmer 1.2s infinite',
                        }}
                    />
                </div>
            </CardCell>
            <CardContentMobile>
                <CardCell>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        {/* Date skeleton */}
                        {createSkeletonPlaceholder('60%', '14px')}

                        {/* Name skeleton */}
                        {createSkeletonPlaceholder('85%', '16px')}

                        {/* Position skeleton */}
                        {createSkeletonPlaceholder('70%', '14px')}

                        {/* Workplace skeleton */}
                        {createSkeletonPlaceholder('75%', '14px')}

                        {/* Status/deadline skeleton */}
                        {createSkeletonPlaceholder('55%', '14px')}

                        {/* Case number skeleton */}
                        {createSkeletonPlaceholder('40%', '12px')}
                    </div>
                </CardCell>
            </CardContentMobile>
        </CardMobile>
    )
}

interface OpenCasesProps {
    showTestCases?: boolean
}

const OpenCases: React.FC<OpenCasesProps> = ({
    showTestCases,
}): JSX.Element => {
    const [filteredCases] = useAtom(filteredCasesAtom)
    const [, fetchCases] = useAtom(fetchCasesAtom)
    const [isLoading] = useAtom(isFetchingCasesAtom)
    const [, setSortedOpenCases] = useAtom(sortedOpenCasesAtom)
    const { setSort, sortState } = useSortCasesList('open')
    const navigate = useNavigate()
    const classes = useStyles()

    useEffect(() => {
        localStorage.setItem('salary-last-tab', 'open')
    }, [])

    useEffect(() => {
        fetchCases()
    }, [fetchCases])

    // Reset sort state to default when component unmounts
    useEffect(() => {
        return () => {
            setSortedOpenCases({
                property: 'Default',
                direction: 'Default',
            })
        }
    }, [setSortedOpenCases])

    const handleSort = (property: string) => (event: React.MouseEvent) => {
        event.preventDefault()
        setSort(property as SortCasesProperties)
        fetchCases()
    }

    if (
        filteredCases.filter(
            (caseItem) => showTestCases || caseItem.caseNumber?.startsWith('LI')
        ).length === 0 &&
        !isLoading
    ) {
        return (
            <div
                style={{
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <Typography size="S">
                    {L('LØN_ÅBNE_SAGER_INGEN_SAGER_LABEL')}
                </Typography>
            </div>
        )
    }

    return (
        <>
            <CardList>
                <CardHeader gridTemplateColumns={gridTemplateColumns}>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('ModtagetDato')}
                            sort={sortState}
                            name="ModtagetDato"
                        >
                            {L(
                                'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_MODATGET_DATO'
                            )}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('NyansatteNavn')}
                            sort={sortState}
                            name="NyansatteNavn"
                        >
                            {L(
                                'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_NYANSATTES_NAVN'
                            )}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <Typography size="XS" weight="bold">
                            <SortButton
                                onClick={handleSort('ArbejdsstedNavn')}
                                sort={sortState}
                                name="ArbejdsstedNavn"
                            >
                                {L(
                                    'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_ARBEJDSSTED'
                                )}
                            </SortButton>
                        </Typography>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <Typography size="XS" weight="bold">
                            <SortButton
                                onClick={handleSort('StillingNavn')}
                                sort={sortState}
                                name="StillingNavn"
                            >
                                {L(
                                    'LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_STILLING'
                                )}
                            </SortButton>
                        </Typography>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('StatusMMNavn')}
                            sort={sortState}
                            name="StatusMMNavn"
                        >
                            {L('LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_STATUS')}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell>
                        <SortButton
                            onClick={handleSort('FristDato')}
                            sort={sortState}
                            name="FristDato"
                        >
                            {L('LØN_ÅBNE_SAGER_TABEL_KOLONNETITEL_FRIST_DATO')}
                        </SortButton>
                    </CardHeaderCell>
                    <CardHeaderCell />
                    <CardHeaderCell />
                </CardHeader>
                {isLoading && (
                    <>
                        {Array.from({ length: 10 }).map((_, index) => (
                            <>
                                <CaseCardSkeleton
                                    key={`skeleton-${Math.random()
                                        .toString(36)
                                        .substr(2, 9)}`}
                                />
                                <CaseCardSkeletonMobile
                                    key={`skeleton-${Math.random()
                                        .toString(36)
                                        .substr(2, 9)}`}
                                />
                            </>
                        ))}
                    </>
                )}
                {!isLoading &&
                    filteredCases
                        .filter(
                            (caseItem) =>
                                showTestCases ||
                                caseItem.caseNumber?.startsWith('LI')
                        )
                        .map((caseItem) => {
                            return (
                                <div key={caseItem.id}>
                                    <Card
                                        key={caseItem.id}
                                        gridTemplateColumns={
                                            gridTemplateColumns
                                        }
                                        useHover
                                        useCursor
                                    >
                                        <CardCell
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            <Typography size="XS">
                                                {formatDate(
                                                    new Date(
                                                        caseItem.receivedDate
                                                    )
                                                )}
                                            </Typography>
                                        </CardCell>
                                        <CardCell
                                            paddingSize="small"
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            <Typography size="XS">
                                                {caseItem.newlyHired?.name}
                                            </Typography>
                                        </CardCell>
                                        <CardCell
                                            paddingSize="small"
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            <Typography size="XS">
                                                {caseItem.newlyHired?.workplace
                                                    ?.navn ?? ''}
                                            </Typography>
                                        </CardCell>
                                        <CardCell
                                            paddingSize="small"
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            <Typography size="XS">
                                                {
                                                    caseItem.newlyHired
                                                        ?.position?.navn
                                                }
                                            </Typography>
                                        </CardCell>
                                        <CardCell
                                            paddingSize="small"
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            {caseItem.caseStatus?.statusNavn.toLocaleLowerCase() !==
                                            'ikkeopdateret' ? (
                                                <Badge
                                                    backgroundColor={
                                                        getCaseStatusColorCode(
                                                            caseItem.caseStatus
                                                                ?.statusType
                                                        ).bgColor
                                                    }
                                                    color={
                                                        getCaseStatusColorCode(
                                                            caseItem.caseStatus
                                                                ?.statusType
                                                        ).color
                                                    }
                                                >
                                                    {caseItem.caseStatus
                                                        ?.statusNavn ?? ''}
                                                </Badge>
                                            ) : null}
                                        </CardCell>
                                        <CardCell
                                            paddingSize="small"
                                            onClick={() => {
                                                navigate(
                                                    `/mine-lonindplaceringer/sag/${caseItem.caseId}`
                                                )
                                            }}
                                        >
                                            <Typography
                                                size="XS"
                                                color={
                                                    caseItem?.caseStatus
                                                        ?.statusType ===
                                                    'FristOverskredet'
                                                        ? colors.caseRed500
                                                        : 'inherit'
                                                }
                                            >
                                                {formatDate(
                                                    new Date(
                                                        caseItem.deadlineDate
                                                    )
                                                )}
                                            </Typography>
                                        </CardCell>
                                        <CardCell paddingSize="none">
                                            <Tooltip content="Flere handlinger">
                                                <div
                                                    style={{
                                                        top: '3px',
                                                        position: 'relative',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <DropdownComponent
                                                        currentCase={caseItem}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </CardCell>
                                        <CardCell paddingSize="none">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: colors.green,
                                                    borderLeft: `1px solid ${colors.borderColorLight}`,
                                                    height: '100%',
                                                }}
                                            >
                                                {caseItem.reminder?.status ===
                                                'Aktiv' ? (
                                                    <Tooltip
                                                        content={
                                                            caseItem?.reminder
                                                                ?.content ?? ''
                                                        }
                                                    >
                                                        <div>
                                                            <IkonAktivreminder
                                                                width="22px"
                                                                fill={
                                                                    colors.green
                                                                }
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                ) : null}
                                                {caseItem.reminder?.status ===
                                                'Passiv' ? (
                                                    <Tooltip
                                                        content={
                                                            caseItem?.reminder
                                                                ?.content ?? ''
                                                        }
                                                    >
                                                        <div>
                                                            <IkonPassivreminder
                                                                width="12px"
                                                                fill={
                                                                    colors.green
                                                                }
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                ) : null}
                                            </div>
                                        </CardCell>
                                    </Card>
                                    <CardMobile
                                        url={`/mine-lonindplaceringer/sag/${caseItem.caseId}`}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: colors.green,
                                                position: 'absolute',
                                                left: '.5rem',
                                                top: '8px',
                                            }}
                                        >
                                            {caseItem.reminder?.status ===
                                            'Aktiv' ? (
                                                <Tooltip
                                                    content={
                                                        caseItem?.reminder
                                                            ?.content ?? ''
                                                    }
                                                >
                                                    <div>
                                                        <IkonAktivreminder
                                                            width="22px"
                                                            fill={colors.green}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            ) : null}
                                            {caseItem.reminder?.status ===
                                            'Passiv' ? (
                                                <Tooltip
                                                    content={
                                                        caseItem?.reminder
                                                            ?.content ?? ''
                                                    }
                                                >
                                                    <div>
                                                        <IkonPassivreminder
                                                            width="12px"
                                                            fill={colors.green}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            ) : null}
                                        </div>
                                        <CardCell>
                                            <Circle>
                                                <CircleText>
                                                    {getInitials(
                                                        caseItem?.newlyHired
                                                            ?.name ?? '??'
                                                    )}
                                                </CircleText>
                                            </Circle>
                                        </CardCell>
                                        <CardContentMobile>
                                            <CardCell>
                                                <Typography size="XS">
                                                    {formatDate(
                                                        new Date(
                                                            caseItem.receivedDate
                                                        )
                                                    )}
                                                </Typography>
                                                <Typography size="XS">
                                                    {caseItem.newlyHired?.name}
                                                </Typography>
                                                <Typography size="XS">
                                                    {caseItem.newlyHired
                                                        ?.position?.navn ?? ''}
                                                </Typography>
                                                <Typography size="XS">
                                                    {caseItem.newlyHired
                                                        ?.workplace?.navn ?? ''}
                                                </Typography>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                    }}
                                                >
                                                    Frist:
                                                    <Typography
                                                        size="XS"
                                                        color={
                                                            caseItem?.caseStatus
                                                                ?.statusType ===
                                                            'FristOverskredet'
                                                                ? colors.caseRed500
                                                                : 'inherit'
                                                        }
                                                    >
                                                        {formatDate(
                                                            new Date(
                                                                caseItem.deadlineDate
                                                            )
                                                        )}
                                                    </Typography>
                                                </div>
                                                <div
                                                    style={{
                                                        marginTop: '0.5rem',
                                                    }}
                                                >
                                                    {caseItem.caseStatus?.statusNavn.toLocaleLowerCase() !==
                                                    'ikkeopdateret' ? (
                                                        <Badge
                                                            backgroundColor={
                                                                getCaseStatusColorCode(
                                                                    caseItem
                                                                        .caseStatus
                                                                        ?.statusType
                                                                ).bgColor
                                                            }
                                                            color={
                                                                getCaseStatusColorCode(
                                                                    caseItem
                                                                        .caseStatus
                                                                        ?.statusType
                                                                ).color
                                                            }
                                                        >
                                                            {caseItem.caseStatus
                                                                ?.statusNavn ??
                                                                ''}
                                                        </Badge>
                                                    ) : null}
                                                </div>
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        right: '0',
                                                        top: '-4px',
                                                    }}
                                                >
                                                    <DropdownComponent
                                                        currentCase={caseItem}
                                                    />
                                                </div>
                                            </CardCell>
                                        </CardContentMobile>
                                    </CardMobile>
                                </div>
                            )
                        })}
            </CardList>
            {filteredCases.filter(
                (caseItem) =>
                    showTestCases || caseItem.caseNumber?.startsWith('LI')
            ).length ? (
                <div className={classes.numberOfMembers}>
                    <Typography size="XS">
                        {L_HTML('LØN_VISER_ANTAL_SAGER_TEKST', {
                            antal: String(
                                filteredCases.filter(
                                    (caseItem) =>
                                        showTestCases ||
                                        caseItem.caseNumber?.startsWith('LI')
                                ).length
                            ),
                        })}
                    </Typography>
                </div>
            ) : null}
        </>
    )
}

export default OpenCases
